import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useEffect} from 'react'
import {useLocation} from 'react-router-dom'
import useUtils from '../../custom-hooks/use-utils-helper'

export const MID = '510004302' // Marketing Cloud MID

/**
 * Custom hook to integrate Marketing Cloud tracking into a React application.
 *
 * This hook performs the following actions:
 * 1. Loads the Marketing Cloud Collect script once when the component mounts.
 * 2. Tracks page views on route changes using the Marketing Cloud `_etmc` object.
 *
 * @function useMarketingCloudTracking
 * @returns {null} This hook does not return any value.
 *
 * @example
 * Usage in a React component
 * const MyComponent = () => {
 *   useMarketingCloudTracking()
 *   return <div>My Component</div>
 * }
 */
export const useMarketingCloudTracking = () => {
    const location = useLocation() //? Get current route
    const {data: customer, isLoading} = useCurrentCustomer() //? Get current customer data
    const {isCart, isCheckout, isCheckoutConfirmation} = useUtils().currentPage(location.pathname)
    const shouldNotTrack = isCart || isCheckout || isCheckoutConfirmation

    useEffect(() => {
        // Load Collect script only once
        if (!document.querySelector('#mc-collect-script')) {
            const collectScript = document.createElement('script')
            collectScript.id = 'mc-collect-script'
            collectScript.src = `https://${MID}.collect.igodigital.com/collect.js`
            collectScript.async = true
            document.head.appendChild(collectScript)
        }
    }, [])

    useEffect(() => {
        // Ensure _etmc exists before pushing events
        if (window._etmc) {
            window._etmc.push(['setOrgId', MID])
            !shouldNotTrack && window._etmc.push(['trackPageView'])
            customer && customer?.email
                ? window._etmc.push(['setUserInfo', {email: customer?.email}])
                : delete window._etmc.user_info
        }
    }, [location.pathname, isLoading])

    return null
}

/**
 * The Marketing Cloud Personalization module of the Salesforce Interactions SDK
 * uses a JavaScript beacon to monitor and track the online behavior of an
 * organization’s web users.
 *
 * The script is loaded only once to prevent duplicate loads.
 */
export const usePersonalizationBeacon = () => {
    useEffect(() => {
        setTimeout(() => {
            // Check if the script is already loaded to avoid duplicate loading
            if (
                document.querySelector(
                    'script[src="//cdn.evgnet.com/beacon/delonghi/delonghi_prod/scripts/evergage.min.js"]',
                )
            ) {
                return
            }

            const script = document.createElement('script')
            script.type = 'text/javascript'
            script.src = '//cdn.evgnet.com/beacon/delonghi/delonghi_prod/scripts/evergage.min.js'
            script.async = true

            document.body.appendChild(script)
        }, 850)
    }, [])
}
